// src/app/slices/projectSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IIdName,
  IProject,
  IProjectAhu,
  ISelectOption,
  ProjectState,
} from '../models';

const initialState: ProjectState = {
  projects: [],
  projects_options: [],
  projectsById: {},
  projectsTimeline: {},
  loading: false,
  error: null,
  tasks: [],
  taskStatuses: [],
  projectsAhus: {},
};

const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setProject: (state, action: PayloadAction<any>) => {
      if (action.payload.id)
        state.projectsById[action.payload.id] = action.payload;
    },
    setTimeline: (state, action: PayloadAction<any>) => {
      if (action.payload.id)
        state.projectsTimeline[action.payload.id] = action.payload.timeline;
    },
    setProjects: (state, action: PayloadAction<IProject[]>) => {
      state.projects = action.payload;
    },
    setProjectsOptions: (state, action: PayloadAction<ISelectOption[]>) => {
      state.projects_options = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setTasks: (state, action: PayloadAction<IIdName[]>) => {
      state.tasks = action.payload;
    },
    setTaskStatuses: (state, action: PayloadAction<IIdName[]>) => {
      state.taskStatuses = action.payload;
    },
    setProjectAhus: (
      state,
      action: PayloadAction<{ id: number; ahus: IProjectAhu[] }>,
    ) => {
      state.projectsAhus[action.payload.id] = action.payload.ahus;
    },
    /** Syncdata put: update project in projectsById and in projects list if present */
    updateProjectFromSync: (state, action: PayloadAction<IProject>) => {
      const project = action.payload;
      if (!project?.id) return;
      state.projectsById[project.id] = project;
      const idx = state.projects.findIndex((p) => p.id === project.id);
      if (idx !== -1) state.projects[idx] = project;
      const option_idx = state.projects_options.findIndex(
        (p) => p.value === project.id,
      );
      if (option_idx !== -1) state.projects_options[idx].label = project.name;
    },
    /** Syncdata post: add project to list and by-id map if not already present */
    addProjectFromSync: (state, action: PayloadAction<IProject>) => {
      const project = action.payload;
      if (!project?.id) return;
      if (state.projectsById[project.id]) return;
      state.projectsById[project.id] = project;
      state.projects.push(project);
      state.projects_options.push({ label: project.name, value: project.id });
    },
    /** Syncdata delete: remove project from list and by-id map */
    removeProjectFromSync: (state, action: PayloadAction<number>) => {
      const id = action.payload;
      state.projects = state.projects.filter((p) => p.id !== id);
      state.projects_options = state.projects_options.filter((p) => p.value !== id);
      delete state.projectsById[id];
    },
  },
});

export const {
  setProject,
  setProjects,
  setProjectsOptions,
  setLoading,
  setError,
  setTimeline,
  setTasks,
  setTaskStatuses,
  setProjectAhus,
  updateProjectFromSync,
  addProjectFromSync,
  removeProjectFromSync,
} = projectSlice.actions;

export default projectSlice.reducer;
